/**
 * The profile's right column: the contact's track record, what the platform
 * knows about them and where it learned it, and the people around them.
 *
 * Every figure is counted from the contact's linked deals and the fields the
 * platform stores; a figure with nothing behind it is left off rather than
 * shown as zero. Reply speed is not here: no message records which way it
 * went, so how fast the contact answers cannot be worked out.
 */
import { useState } from 'react'
import { type UseQueryResult } from '@tanstack/react-query'
import clsx from 'clsx'
import { Pin, RefreshCw } from 'lucide-react'

import { type CrmContact, type CrmEnrichmentHistory } from '@/services/crm'
import { formatUsd } from '@/utils/money'
import { formatDate, relativeTime } from './crmFields'
import OrganizationPeople from './OrganizationPeople'
import { EnrichmentHistory } from './ProfileSections'
import { knownFacts, type KnownFact, type TrackRecord as TrackRecordFacts, count } from './profileFacts'
import { Absent, Card, FOCUS, QUIET_BUTTON } from './profileUi'

// ── Track record ──────────────────────────────────────────────────────────────

const MIX_SHADES = ['bg-amber-400', 'bg-stone-300', 'bg-stone-500', 'bg-stone-700']
const MIX_SHOWN = 4

function Tile({ value, label, note }: { value: string | null; label: string; note?: string }) {
  return (
    <div className="min-w-0 flex-1 basis-[84px] rounded-lg border border-stone-800 px-2.5 py-2.5">
      <div className="whitespace-nowrap text-[18px] font-semibold leading-tight tabular-nums text-stone-100">{value ?? <Absent />}</div>
      <div className="mt-0.5 text-[11px] text-stone-400">{label}</div>
      {note ? <div className="mt-0.5 text-[10.5px] leading-snug text-stone-600">{note}</div> : null}
    </div>
  )
}

export function TrackRecordCard({ record }: { record: TrackRecordFacts }) {
  if (record.deals === 0) {
    return (
      <Card title="Track record" kicker>
        <p className="text-[12px] text-stone-500">No deals are linked to this contact.</p>
      </Card>
    )
  }
  const shown = record.mix.slice(0, MIX_SHOWN)
  const other = record.mix.slice(MIX_SHOWN).reduce((sum, m) => sum + m.count, 0)
  const segments = other > 0 ? [...shown, { label: `${record.mix.length - MIX_SHOWN} more`, count: other }] : shown
  return (
    <Card title="Track record" kicker>
      <div className="flex flex-wrap gap-2">
        <Tile value={String(record.deals)} label="Deals linked" />
        {record.funded !== null ? (
          <Tile
            value={String(record.funded)}
            label="Funded"
            note={record.stagesKnown < record.deals ? `Stage on record for ${record.stagesKnown} of ${record.deals}` : undefined}
          />
        ) : null}
        <Tile
          value={record.volume !== null ? formatUsd(record.volume) : null}
          label="Volume"
          note={record.volume !== null ? `Amount on ${record.amountsKnown} of ${record.deals} deals` : 'No deal states an amount'}
        />
      </div>
      {record.funded === null ? (
        <p className="mt-2 text-[11px] leading-snug text-stone-500">No linked deal has a stage on record, so funded deals cannot be counted.</p>
      ) : null}

      <div className="mt-4">
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <span className="text-[12px] font-medium text-stone-200">Asset types</span>
          <span className="text-[11px] text-stone-500">{count(record.assetTypesKnown, 'deal')}</span>
        </div>
        {segments.length === 0 ? (
          <p className="text-[11.5px] text-stone-500">Asset type is not on record for these deals.</p>
        ) : (
          <>
            <div
              role="img"
              aria-label={`Asset types: ${segments.map((s) => `${s.label} ${s.count}`).join(', ')}`}
              className="flex h-2.5 gap-0.5 overflow-hidden rounded-full"
            >
              {segments.map((s, index) => (
                <span
                  key={s.label}
                  className={MIX_SHADES[index] ?? 'bg-stone-700'}
                  style={{ width: `${(s.count / record.assetTypesKnown) * 100}%` }}
                />
              ))}
            </div>
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[11.5px] text-stone-300">
              {segments.map((s, index) => (
                <li key={s.label} className="inline-flex items-center gap-1.5">
                  <span aria-hidden className={clsx('h-2 w-2 rounded-sm', MIX_SHADES[index] ?? 'bg-stone-700')} />
                  {s.label} <span className="tabular-nums text-stone-500">{s.count}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        {record.assetTypesKnown < record.deals && segments.length > 0 ? (
          <p className="mt-1.5 text-[10.5px] text-stone-600">
            {count(record.deals - record.assetTypesKnown, 'deal')} with no asset type on record.
          </p>
        ) : null}
      </div>
    </Card>
  )
}

// ── What we know ──────────────────────────────────────────────────────────────

function FactRow({ fact, pending, quiet = false }: { fact: KnownFact; pending: boolean; quiet?: boolean }) {
  const where = fact.pinned
    ? `Set by hand${fact.observedAt ? ` · ${formatDate(fact.observedAt)}` : ''}`
    : fact.source
      ? `${fact.source}${fact.observedAt ? ` · ${formatDate(fact.observedAt)}` : ''}`
      : pending
        ? 'Reading the source…'
        : 'Source not recorded'
  return (
    <li className={quiet ? '' : 'border-t border-stone-800/80 pt-2.5 first:border-t-0 first:pt-0'}>
      <div className="text-[11px] text-stone-500">{fact.label}</div>
      <div className={clsx('break-words', quiet ? 'text-[12px] text-stone-300' : 'text-[13px] text-stone-100')}>{fact.text}</div>
      <div className="mt-0.5 flex items-center gap-1 text-[11px] text-stone-500">
        {fact.pinned ? <Pin size={10} className="text-amber-400/80" aria-hidden /> : null}
        {where}
      </div>
    </li>
  )
}

export function WhatWeKnow({
  contact,
  trail,
  onReenrich,
  reenrichPending,
}: {
  contact: CrmContact
  trail: UseQueryResult<CrmEnrichmentHistory>
  onReenrich: () => void
  reenrichPending: boolean
}) {
  const [historyOpen, setHistoryOpen] = useState(false)
  const facts = knownFacts(contact, trail.data?.observations ?? null)
  const e = contact.enrichment
  const answered = (e?.sources ?? []).filter((s) => s.ok).map((s) => s.name)
  const failed = (e?.sources ?? []).filter((s) => !s.ok && s.detail)
  return (
    <Card
      title="What we know"
      kicker
      action={
        <button type="button" onClick={onReenrich} disabled={reenrichPending} title="Look this contact up externally" className={clsx(QUIET_BUTTON, '!px-2 !py-1 !text-[11px]')}>
          <RefreshCw size={11} className={reenrichPending ? 'motion-safe:animate-spin' : undefined} aria-hidden />
          {reenrichPending ? 'Looking up…' : 'Re-check sources'}
        </button>
      }
    >
      {facts.length === 0 ? (
        <p className="text-[12px] leading-snug text-stone-500">
          {e?.lookedUpAt
            ? 'A lookup ran and added nothing beyond what is on the card.'
            : 'Nothing beyond the card is on record yet. Re-check sources looks this contact up.'}
        </p>
      ) : (
        <>
          {facts.some((f) => f.group === 'stated') ? (
            <ul className="space-y-2.5">
              {facts
                .filter((f) => f.group === 'stated')
                .map((fact) => (
                  <FactRow key={fact.key} fact={fact} pending={trail.isPending} />
                ))}
            </ul>
          ) : null}
          {facts.some((f) => f.group === 'observed') ? (
            <div className={clsx(facts.some((f) => f.group === 'stated') && 'mt-4 border-t border-stone-800/80 pt-3')}>
              <div className="mb-2 font-mono text-[9.5px] uppercase tracking-[0.14em] text-stone-600">Also observed</div>
              <ul className="space-y-2">
                {facts
                  .filter((f) => f.group === 'observed')
                  .map((fact) => (
                    <FactRow key={fact.key} fact={fact} pending={trail.isPending} quiet />
                  ))}
              </ul>
            </div>
          ) : null}
        </>
      )}

      <p className="mt-4 text-[11px] leading-snug text-stone-500">
        {e?.lookedUpAt ? `External lookup last run ${relativeTime(e.lookedUpAt)}. ` : 'No external lookup has been run for this contact. '}
        {answered.length > 0 ? `Answered by ${answered.join(', ')}.` : ''}
      </p>
      {failed.length > 0 ? (
        <ul className="mt-1 space-y-0.5 text-[11px] text-stone-500">
          {failed.map((s) => (
            <li key={s.name}>
              <span className="text-stone-400">{s.name}:</span> {s.detail}
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        aria-expanded={historyOpen}
        onClick={() => setHistoryOpen((v) => !v)}
        className={clsx('mt-3 rounded text-[11.5px] text-amber-300 hover:text-amber-200', FOCUS)}
      >
        {historyOpen ? 'Hide lookup history' : 'Show lookup history'}
      </button>
      {historyOpen ? <EnrichmentHistory contact={contact} state={trail} /> : null}
    </Card>
  )
}

// ── People at the firm ────────────────────────────────────────────────────────

/**
 * The firm's org chart, open from the start (CY-24, 26 Oct: "you're gonna get
 * an org chart right off the bat"): the firm on top, its offices where its
 * people sit in more than one, and its people, with this contact marked and
 * their office open. The same component the side panel and the deal's Parties
 * page draw, from the same read, so the three cannot disagree; its List view
 * keeps each person's basis and deals.
 */
export function PeopleAtFirm({
  contact,
  onOpenContact,
}: {
  contact: CrmContact
  onOpenContact?: (id: string) => void
}) {
  const isOrganisation = contact.kind === 'organisation' || Boolean(contact.isEntity)
  const name = isOrganisation ? contact.displayName : contact.company
  if (!name) return null
  const title = isOrganisation ? 'People under this organization' : `People at ${name}`

  return (
    <Card title={title} kicker>
      <OrganizationPeople
        name={name}
        focusId={isOrganisation ? null : contact.id}
        showName={false}
        onOpenContact={onOpenContact}
      />
    </Card>
  )
}
